import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { DataTable, ErrorState, Pagination, Spinner } from '../../components/ui.jsx';
import { useFetch, useTitle } from '../../lib/hooks.js';
import { formatDateTime } from '../../lib/format.js';

const ACTION_LABELS = {
  'manager.invited': 'Invited',
  'manager.revoked': 'Access revoked',
  'manager.reactivated': 'Access restored',
  'manager.access_changed': 'Changed what they are in charge of',
  'property.created': 'Added a property',
  'property.updated': 'Updated a property',
  'property.status_changed': 'Changed a property status',
  'property.deleted': 'Deleted a property',
  'unit.created': 'Added a unit',
  'unit.updated': 'Updated a unit',
  'unit.deleted': 'Deleted a unit',
  'lease.created': 'Drew up a lease',
  'charge.created': 'Added a charge',
  'charge.waived': 'Waived a charge',
  'payment.recorded': 'Recorded a payment',
  'payment.voided': 'Voided a payment',
  'payment.intent_confirmed': 'Confirmed a payment',
  'application.decided': 'Decided an application',
  'tenant.created': 'Added a tenant',
  'tenant.password_reset': "Reset a tenant's password",
  'utility.account_created': 'Put somebody on a meter',
  'utility.bill_drafted': 'Read a meter and drew up a bill',
  'utility.bill_posted': 'Sent a utility bill to the customer',
  'utility.bill_corrected': 'Corrected a utility bill',
  'utility.bill_cancelled': 'Cancelled a utility bill',
  'utility.bill_removed': 'Removed a utility bill',
  'utility.bill_withdrawn': 'Took a sent bill back for correction',
  'utility.billing_run': 'Billed a whole meter for the month',
  'utility.bills_posted': 'Sent a month of bills out',
  'utility.claim_confirmed': 'Confirmed a payment a customer reported',
  'utility.claim_rejected': 'Turned down a payment a customer reported',
  'utility.payment_recorded': 'Recorded a utility payment',
  'utility.payment_voided': 'Cancelled a utility receipt',
  'utility.disconnected': 'Cut off a service',
  'utility.reconnected': 'Put a service back on',
  'utility.main_meter_read': 'Read the main meter',
  'utility.main_meter_removed': 'Removed a main meter reading',
  'utility.reading_photographed': 'Photographed a meter reading',
  'utility.account_removed': 'Took a customer off a meter',
  'utility.service_removed': 'Removed a utility service',
  'utility.late_fee_waived': 'Dropped a late fee',
  'announcement.posted': 'Put out a notice to the tenants',
  'announcement.withdrawn': 'Took a notice down'
};

const describe = (entry) => {
  const label = ACTION_LABELS[entry.action] || entry.action;
  if (!entry.details) return label;
  const parts = Object.entries(entry.details)
    .map(([key, value]) => `${key}: ${value}`)
    .join(', ');
  return `${label} (${parts})`;
};

const ManagerActivity = () => {
  const { id } = useParams();
  useTitle(id ? 'Manager activity' : 'Team activity');
  const [page, setPage] = useState(1);

  const path = id ? `/managers/${id}/activity?page=${page}&pageSize=30` : `/managers/activity?page=${page}&pageSize=30`;
  const { data, loading, error, reload } = useFetch(path);
  const managers = useFetch(id ? '/managers' : null);
  const manager = id ? (managers.data?.items || []).find((item) => String(item.id) === id) : null;

  if (loading && !data) return <Spinner />;
  if (error) {
    return (
      <div className="container section">
        <ErrorState message={error} onRetry={reload} />
      </div>
    );
  }

  const items = data.items || [];

  const columns = [
    {
      key: 'when',
      header: 'When',
      render: (entry) => <span className="muted">{formatDateTime(entry.createdAt)}</span>
    },
    ...(id
      ? []
      : [
          {
            key: 'manager',
            header: 'Manager',
            render: (entry) => entry.managerName
          }
        ]),
    {
      key: 'action',
      header: 'Action',
      render: (entry) => describe(entry)
    }
  ];

  return (
    <div className="container section">
      <header className="page-head">
        <div>
          <h1>{manager ? `${manager.name}'s activity` : 'Team activity'}</h1>
          <p className="muted">
            {manager
              ? `Everything ${manager.name} has done on your account.`
              : 'Everything your managers have done, and every time you have invited, revoked or restored one.'}
          </p>
        </div>
        <Link to="/landlord/team" className="btn btn-outline">
          Back to team
        </Link>
      </header>

      <DataTable
        columns={columns}
        rows={items}
        empty={{
          title: 'No activity yet',
          message: 'Once a manager starts working on your account, everything they do shows up here.'
        }}
      />

      <Pagination page={data.page} totalPages={data.totalPages} onChange={setPage} />
    </div>
  );
};

export default ManagerActivity;
